import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLocation as useLocationHook } from '../../contexts/LocationContext';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import AuthButton from '../auth/AuthButton';
import DataDownloadModal from '../auth/DataDownloadModal';
import { NotificationBell } from '../notifications/Notifications';
import './Header.css';
import { HeaderLocationSelector } from './HeaderLocationSelector';

interface HeaderProps {
  onNavigateToOverview: () => void;
  onLocationSelect: (location: string) => void;
  currentLocation?: string;
}

export const Header: React.FC<HeaderProps> = ({ 
  onNavigateToOverview, 
  onLocationSelect,
  currentLocation = ''
}) => {
  const location = useLocation();
  const { selectedGeofips } = useLocationHook();
  const { user } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [isScrolled, setIsScrolled] = useState(false);
  const [showHamburgerMenu, setShowHamburgerMenu] = useState(false);
  const [showDataDownload, setShowDataDownload] = useState(false);

  // Check if we're on the landing page
  const isLandingPage = location.pathname === '/';

  // Derive current page/section for Download Data modal (transportation, buildings, waste/other, people, overview)
  const pathParts = location.pathname.split('/').filter(Boolean);
  const downloadPageSections = new Set(['transportation', 'buildings', 'people', 'overview', 'other']);
  let currentPageForDownload: string | undefined;
  if (pathParts.length >= 2) {
    currentPageForDownload = pathParts[pathParts.length - 1]; // e.g. /oakland/transportation -> transportation, /oakland/other -> other
  } else if (pathParts.length === 1) {
    const segment = pathParts[0];
    currentPageForDownload = downloadPageSections.has(segment) ? segment : 'overview'; // /transportation -> transportation, /other -> other, /oakland -> overview
  }
  // pathParts.length === 0 (landing '/') -> undefined

  // Handle scroll detection
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      setIsScrolled(scrollTop > 100);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleHamburgerMenu = () => {
    setShowHamburgerMenu(!showHamburgerMenu);
  };

  // Close hamburger menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showHamburgerMenu) {
        const target = event.target as Element;
        if (!target.closest('.hamburger-menu')) {
          setShowHamburgerMenu(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showHamburgerMenu]);

  return (
    <header className={`header ${isScrolled ? 'scrolled' : ''}`} role="banner">
      <div className="header-content">
        {/* Desktop Layout */}
        <div className="header-desktop">
          <Link to="/" className="header-title-section" aria-label="ClimatePlans.org home">
            <img 
              src="/data/coolclimate-logo.png" 
              alt="" 
              className="header-logo"
            />
            <div className="header-text">
              <h1>ClimatePlans.org <span style={{ color: 'red', fontSize: '1.2rem', fontWeight: 'bold' }}>Beta</span></h1>
              <p className="header-description">Climate Action Planning Data and Resources for California cities and counties</p>
            </div>
          </Link>
          
          <div className="header-actions">
            {/* Location selector - show on all pages except landing page */}
            {!isLandingPage && (
              <div className="header-location-section">
                {!isScrolled ? (
                  <HeaderLocationSelector
                    onLocationSelect={onLocationSelect}
                    currentLocation={currentLocation}
                  />
                ) : (
                  <div className="hamburger-menu">
                    <button 
                      type="button"
                      className={`hamburger-button ${showHamburgerMenu ? 'active' : ''}`}
                      onClick={toggleHamburgerMenu}
                      aria-label={`Change location from ${currentLocation || 'current location'}`}
                      aria-expanded={showHamburgerMenu}
                      title={`Current: ${currentLocation || 'No location selected'}\nClick to change`}
                    >
                      <span className="hamburger-icon" aria-hidden="true">📍</span>
                      {currentLocation && (
                        <span className="current-location-indicator">
                          {currentLocation.length > 12 ? currentLocation.substring(0, 12) + '...' : currentLocation}
                        </span>
                      )}
                    </button>
                    {showHamburgerMenu && (
                      <div className="hamburger-dropdown">
                        <div className="hamburger-theme-section">
                          <button
                            type="button"
                            className="hamburger-theme-button"
                            onClick={() => {
                              toggleTheme();
                              setShowHamburgerMenu(false);
                            }}
                            title={theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
                          >
                            {theme === 'light' ? '🌙 Dark mode' : '☀️ Light mode'}
                          </button>
                        </div>
                        <div className="hamburger-location-selector">
                          <HeaderLocationSelector
                            onLocationSelect={onLocationSelect}
                            currentLocation={currentLocation}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Dark mode toggle - always visible, before Download so it doesn't get pushed off when signed in */}
            <div className="header-theme-section">
              <button
                type="button"
                className="header-theme-button"
                onClick={toggleTheme}
                title={theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
                aria-label={theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
              >
                {theme === 'light' ? '🌙 Dark mode' : '☀️ Light mode'}
              </button>
            </div>

            {/* Data download button - show only when logged in and location is selected */}
            {user && !isLandingPage && currentLocation && selectedGeofips && (
              <div className="header-download-section">
                <button
                  type="button"
                  className="header-download-button"
                  onClick={() => setShowDataDownload(true)}
                  title="Download all data for this location"
                  aria-label="Download all data for this location"
                >
                  <span aria-hidden="true">📥</span> Download Data
                </button>
              </div>
            )}

            {/* Notifications */}
            <div className="header-notifications-section">
              <NotificationBell />
            </div>
            
            {/* Authentication button */}
            <div className="header-auth-section">
              <AuthButton />
            </div>
          </div>
        </div>

        {/* Mobile Layout */}
        <div className="header-mobile">
          {/* Left: ClimatePlans title */}
          <Link to="/" className="header-title-section" aria-label="ClimatePlans.org home">
            <h1>ClimatePlans <span style={{ color: 'red', fontSize: '0.9rem', fontWeight: 'bold' }}>Beta</span></h1>
          </Link>
          
          {/* Center: Location selector - show on all pages except landing page */}
          {!isLandingPage && (
            <div className="header-location-section">
              <HeaderLocationSelector
                onLocationSelect={onLocationSelect}
                currentLocation={currentLocation}
              />
            </div>
          )}
          
          {/* Right: Menu button */}
          <div className="header-menu-section">
            <div className="hamburger-menu">
              <button 
                type="button"
                className={`hamburger-button ${showHamburgerMenu ? 'active' : ''}`}
                onClick={toggleHamburgerMenu}
                aria-label="Open menu"
                aria-expanded={showHamburgerMenu}
              >
                <div className="hamburger-icon">
                  <span className="hamburger-line"></span>
                  <span className="hamburger-line"></span>
                  <span className="hamburger-line"></span>
                </div>
              </button>
              {showHamburgerMenu && (
                <div className="hamburger-dropdown">
                  {/* Dark mode toggle */}
                  <div className="hamburger-theme-section">
                    <button
                      type="button"
                      className="hamburger-theme-button"
                      onClick={() => {
                        toggleTheme();
                        setShowHamburgerMenu(false);
                      }}
                    >
                      {theme === 'light' ? '🌙 Dark mode' : '☀️ Light mode'}
                    </button>
                  </div>
                  {/* Data download - only show when logged in and location is selected */}
                  {user && currentLocation && selectedGeofips && (
                    <div className="hamburger-download-section">
                      <button
                        type="button"
                        className="hamburger-download-button"
                        onClick={() => {
                          setShowDataDownload(true);
                          setShowHamburgerMenu(false);
                        }}
                        aria-label="Download all data for this location"
                      >
                        <span aria-hidden="true">📥</span> Download Data
                      </button>
                    </div>
                  )}
                  
                  {/* Authentication section */}
                  <div className="hamburger-auth-section">
                    <AuthButton />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Data Download Modal */}
      <DataDownloadModal
        isOpen={showDataDownload}
        onClose={() => setShowDataDownload(false)}
        location={currentLocation}
        geofips={selectedGeofips}
        currentPage={currentPageForDownload}
      />
    </header>
  );
};
